import { useAuth } from '@/contexts/AuthContext';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Lock, Info } from 'lucide-react';
import { Link } from 'react-router-dom';

/**
 * Banner that shows billing state warnings for past_due and suspended accounts
 * Displays countdown for grace period and appropriate CTAs
 */
export const BillingStateBanner = () => {
  const { user } = useAuth();
  const org = user?.organization;

  // Only show for non-active states
  if (!org || org.billing_state === 'active' || !org.billing_state) {
    return null;
  }

  // Calculate days remaining in grace period
  const getDaysRemaining = () => {
    if (!org.grace_period_ends_at) return null;
    const endDate = new Date(org.grace_period_ends_at);
    const now = new Date();
    const diffTime = endDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  const daysRemaining = getDaysRemaining();

  // Past Due (Grace Period Active)
  if (org.billing_state === 'past_due') {
    return (
      <Alert className="border-orange-500 bg-orange-50 dark:bg-orange-950 rounded-none border-l-4 border-t-0 border-r-0 border-b-0">
        <AlertTriangle className="h-5 w-5 text-orange-600 dark:text-orange-400" />
        <AlertTitle className="text-orange-900 dark:text-orange-100 font-semibold">
          Payment Issue - Update Billing to Continue Service
        </AlertTitle>
        <AlertDescription className="text-orange-800 dark:text-orange-200">
          <div className="flex items-center justify-between">
            <div>
              {daysRemaining !== null && daysRemaining > 0 ? (
                <p>
                  Your payment failed. You have <strong>{daysRemaining} day{daysRemaining !== 1 ? 's' : ''}</strong> remaining 
                  in your grace period. After that, your account will be suspended and you'll lose access to create new content.
                </p>
              ) : (
                <p>
                  Your grace period has ended. Please update your payment immediately to restore full access.
                </p>
              )}
            </div>
            <Button asChild variant="default" className="ml-4 shrink-0">
              <Link to="/subscription">
                Update Payment
              </Link>
            </Button>
          </div>
        </AlertDescription>
      </Alert>
    );
  }

  // Suspended (Read-Only Mode)
  if (org.billing_state === 'suspended') {
    return (
      <Alert className="border-red-500 bg-red-50 dark:bg-red-950 rounded-none border-l-4 border-t-0 border-r-0 border-b-0">
        <Lock className="h-5 w-5 text-red-600 dark:text-red-400" />
        <AlertTitle className="text-red-900 dark:text-red-100 font-semibold">
          Account Suspended - Read-Only Mode
        </AlertTitle>
        <AlertDescription className="text-red-800 dark:text-red-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="mb-2">
                Your subscription payment is overdue. You can view existing data but cannot create new content or submit orders.
              </p>
              <p className="text-sm">
                Update your payment to restore full access. All your data is preserved.
              </p>
            </div>
            <Button asChild variant="default" className="ml-4 shrink-0">
              <Link to="/subscription">
                Restore Access
              </Link>
            </Button>
          </div>
        </AlertDescription>
      </Alert>
    );
  }

  // Invoice Sent (Awaiting Payment)
  if (org.billing_state === 'invoice_sent') {
    return (
      <Alert className="border-blue-500 bg-blue-50 dark:bg-blue-950 rounded-none border-l-4 border-t-0 border-r-0 border-b-0">
        <Info className="h-5 w-5 text-blue-600 dark:text-blue-400" />
        <AlertTitle className="text-blue-900 dark:text-blue-100 font-semibold">
          Invoice Pending
        </AlertTitle>
        <AlertDescription className="text-blue-800 dark:text-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <p>
                Your invoice has been sent. Once payment is received, you'll have full access to all features.
              </p>
            </div>
            <Button asChild variant="outline" className="ml-4 shrink-0">
              <Link to="/subscription">
                View Invoice
              </Link>
            </Button>
          </div>
        </AlertDescription>
      </Alert>
    );
  }

  // Pending (Onboarding)
  if (org.billing_state === 'pending') {
    return (
      <Alert className="border-gray-500 bg-gray-50 dark:bg-gray-950 rounded-none border-l-4 border-t-0 border-r-0 border-b-0">
        <Info className="h-5 w-5 text-gray-600 dark:text-gray-400" />
        <AlertTitle className="text-gray-900 dark:text-gray-100 font-semibold">
          Setup In Progress
        </AlertTitle>
        <AlertDescription className="text-gray-800 dark:text-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p>
                Your account is being set up. Once your invoice is sent and paid, you'll have full access.
              </p>
            </div>
            <Button asChild variant="outline" className="ml-4 shrink-0">
              <Link to="/contact">
                Contact Support
              </Link>
            </Button>
          </div>
        </AlertDescription>
      </Alert>
    );
  }

  // Canceled
  if (org.billing_state === 'canceled') {
    return (
      <Alert className="border-red-500 bg-red-50 dark:bg-red-950 rounded-none border-l-4 border-t-0 border-r-0 border-b-0">
        <Lock className="h-5 w-5 text-red-600 dark:text-red-400" />
        <AlertTitle className="text-red-900 dark:text-red-100 font-semibold">
          Subscription Canceled
        </AlertTitle>
        <AlertDescription className="text-red-800 dark:text-red-200">
          <div className="flex items-center justify-between">
            <div>
              <p>
                Your subscription has been canceled. Contact support to reactivate your account.
              </p>
            </div>
            <Button asChild variant="default" className="ml-4 shrink-0">
              <Link to="/contact">
                Contact Support
              </Link>
            </Button>
          </div>
        </AlertDescription>
      </Alert>
    );
  }

  return null;
};
